/**
 * PrescriptionForm — 별지 제10호 처방전 양식 렌더(테스트용, chu_bang.png 미러).
 * 흰 배경·진한 글자로 모바일 OCR 촬영 대비. 약명 표 + 용법으로 OCR 약명·슬롯 추출 보조.
 */
import { CLINIC, DiseaseT, slotsKo } from './prescriptionTemplates'
import { useLabel } from './framework/useLabel'

interface Props {
  patientName: string
  disease: DiseaseT
  issuedDate: string // YYYY-MM-DD
  issueNo: number
}

const cell: React.CSSProperties = { border: '1px solid #222', padding: '4px 8px', fontSize: 13, color: '#111' }
const th: React.CSSProperties = { ...cell, background: '#f2f2f2', fontWeight: 700, textAlign: 'center' }

export default function PrescriptionForm({ patientName, disease, issuedDate, issueNo }: Props) {
  const { t } = useLabel()
  return (
    <div style={{ background: '#fff', color: '#111', padding: 20, width: 660, margin: '0 auto', fontFamily: 'Malgun Gothic, sans-serif' }}>
      <div style={{ fontSize: 11, marginBottom: 4 }}>{t('SIM_PRESC_FORM_NO', '[별지 제10호 서식]')}</div>
      <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 800, letterSpacing: 16, margin: '6px 0 12px' }}>{t('SIM_PRESC_TITLE', '처 방 전')}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
        <span>{t('SIM_PRESC_INSURANCE', '(1) 의료보험')}</span>
        <span>{t('SIM_PRESC_ORG_CODE', '요양기관기호')} : {CLINIC.orgCode}</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={th}>{t('SIM_PRESC_ISSUE_DATE', '교부일자')}</td><td style={cell}>{issuedDate}</td>
            <td style={th}>{t('SIM_PRESC_ORG', '의료기관')}</td><td style={cell}>{CLINIC.name}</td>
          </tr>
          <tr>
            <td style={th}>{t('SIM_PRESC_ISSUE_NO', '교부번호')}</td><td style={cell}>{t('SIM_PRESC_NO_UNIT', '제')} {issueNo} {t('SIM_PRESC_NO_SUFFIX', '호')}</td>
            <td style={th}>{t('SIM_PRESC_PHONE', '전화')}</td><td style={cell}>{CLINIC.phone}</td>
          </tr>
          <tr>
            <td style={th}>{t('SIM_PRESC_PATIENT_NAME', '환자 성명')}</td><td style={cell}>{patientName || '홍길동'}</td>
            <td style={th}>{t('SIM_PRESC_FAX', '팩스')}</td><td style={cell}>{CLINIC.fax}</td>
          </tr>
          <tr>
            <td style={th}>{t('SIM_PRESC_RRN', '주민등록번호')}</td><td style={cell}>______-1******</td>
            <td style={th}>{t('SIM_PRESC_EMAIL', 'e-mail')}</td><td style={cell}>{CLINIC.email}</td>
          </tr>
          <tr>
            <td style={th}>{t('SIM_PRESC_DISEASE_CODE', '질병분류기호')}</td><td style={cell}>{disease.code}</td>
            <td style={th}>{t('SIM_PRESC_DOCTOR_NAME', '처방의료인 성명')}</td><td style={cell}>{CLINIC.doctor} {t('SIM_PRESC_SEAL', '(인)')}</td>
          </tr>
          <tr>
            <td style={th}>{t('SIM_PRESC_LICENSE_NO', '면허번호')}</td><td style={cell}>{CLINIC.license}</td>
            <td style={th}>{t('SIM_PRESC_SIGNATURE', '서명 또는 날인')}</td><td style={cell}> </td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <td style={{ ...th, width: '44%' }}>{t('SIM_PRESC_MED_NAME', '처방 의약품의 명칭')}</td>
            <td style={th}>{t('SIM_PRESC_DOSE_PER', '1회 투약량')}</td>
            <td style={th}>{t('SIM_PRESC_DAILY_COUNT', '1일 투여횟수')}</td>
            <td style={th}>{t('SIM_PRESC_TOTAL_DAYS', '총 투약일수')}</td>
            <td style={th}>{t('SIM_PRESC_SUBSTITUTE', '대체')}</td>
          </tr>
        </thead>
        <tbody>
          {disease.items.map((it, i) => (
            <tr key={i}>
              <td style={cell}>
                {it.name}
                <div style={{ fontSize: 11, color: '#555' }}>{t('SIM_PRESC_USAGE', '용법')}: {slotsKo(it.slots)} · {it.instruction}</div>
              </td>
              <td style={{ ...cell, textAlign: 'center' }}>{it.dose.toFixed(2)}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{it.dailyCount}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{it.durationDays}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{t('SIM_PRESC_NOT_ALLOWED', '불가')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ fontSize: 11, marginTop: 8, color: '#444' }}>
        {t('SIM_PRESC_FOOTNOTE', '※ 환자의 요구가 있는 때에는 질병분류기호를 기재하지 아니합니다. · 본 처방전은 테스트용으로 발행되었습니다.')}
      </div>
    </div>
  )
}
